/**
 * User Service Layer
 * Handles all user-related API calls with proper error handling
 */

import { USERS_API_BASE_URL } from '../constants';
import {
  User,
  UserListResponse,
  UserDetailResponse,
  DeleteUserResponse,
  SwitchUserResponse,
} from '../types/user.types';

/**
 * Fetch users based on user type and organization
 */
export const fetchUsers = async (
  token: string,
  userId: string | null,
  orgId: string,
  userType: string
): Promise<User[]> => {
  try {
    let url = '';
    
    if (userType === 'OWNER' || userType === 'INTERNALADMIN') {
      // Super admin or internal admin - use admin endpoint
      url = `${USERS_API_BASE_URL}/admin/${orgId}`;
    } else {
      // Regular user - use base endpoint
      url = `${USERS_API_BASE_URL}/`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch users: ${response.statusText}`);
    }

    const data: UserListResponse = await response.json();
    
    if (data.status === 302 || !data.userList) {
      throw new Error(data.error || 'Failed to load users');
    }

    return data.userList;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error instanceof Error ? error : new Error('Failed to fetch users');
  }
};

/**
 * Fetch user detail by UUID
 */
export const fetchUserDetail = async (
  token: string,
  uuid: string
): Promise<UserDetailResponse> => {
  try {
    const response = await fetch(`${USERS_API_BASE_URL}/user-detail/${uuid}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user detail: ${response.statusText}`);
    }

    const data: UserDetailResponse = await response.json();

    if (data.status === 302) {
      throw new Error(data.error || 'User not found');
    }

    return data;
  } catch (error) {
    console.error('Error fetching user detail:', error);
    throw error instanceof Error ? error : new Error('Failed to fetch user detail');
  }
};

/**
 * Delete a user by ID
 */
export const deleteUser = async (
  token: string,
  userId: string
): Promise<DeleteUserResponse> => {
  try {
    const response = await fetch(`${USERS_API_BASE_URL}/deleteUser/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
      },
      body: '',
    });

    if (!response.ok) {
      throw new Error(`Failed to delete user: ${response.statusText}`);
    }

    const data: DeleteUserResponse = await response.json();

    return data;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error instanceof Error ? error : new Error('Failed to delete user');
  }
};

/**
 * Switch to a different user (impersonation)
 */
export const switchUser = async (
  token: string,
  uuid: string
): Promise<SwitchUserResponse> => {
  try {
    const response = await fetch(`${USERS_API_BASE_URL}/switch-user/${uuid}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to switch user: ${response.statusText}`);
    }

    const data: SwitchUserResponse = await response.json();

    if (data.status === 302) {
      throw new Error(data.error || 'Failed to switch user');
    }

    return data;
  } catch (error) {
    console.error('Error switching user:', error);
    throw error instanceof Error ? error : new Error('Failed to switch user');
  }
};
